// ============================================================
//  src/components/layout/AppLayout.js
// ============================================================
import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AppLayout.css';

const NAV = [
  { to: '/dashboard',      icon: '▦',  label: 'Dashboard'       },
  { to: '/inbox',          icon: '💬', label: 'Inbox'           },
  { to: '/rent-space',     icon: '🏠', label: 'Rent Space'      },
  { to: '/community',      icon: '🌐', label: 'Community'       },
  { to: '/collaborations', icon: '⇄',  label: 'Collaborations'  },
  { to: '/integrations',   icon: '⚡', label: 'AI Integrations' },
  { to: '/billing',        icon: '◈',  label: 'Billing'         },
];

const NAV_BOTTOM = [
  { to: '/how-to-use',        icon: '📖', label: 'How to Use'        },
  { to: '/meta-verification', icon: '🧭', label: 'Meta Verification' },
  { to: '/settings/webhook',  icon: '🔗', label: 'Webhook'           },
  { to: '/contact',           icon: '💬', label: 'Contact Us'        },
  { to: '/profile',           icon: '◉',  label: 'Profile'           },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The Inbox wants a full-bleed, WhatsApp-Web-style layout (no page
  // gutters, fills the viewport, internal panels scroll independently)
  // instead of the centered/padded max-width column every other page
  // uses. Computed in JS (not just a CSS media query) because this
  // page's layout depends on knowing the exact mobile topbar height
  // to subtract, and — as this project has repeatedly run into —
  // className-based layout rules can silently fail to apply, so the
  // load-bearing height/overflow values are set as inline styles here.
  const isInboxRoute = location.pathname.startsWith('/inbox');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Belt-and-suspenders: directly lock document/body scrolling while on
  // the Inbox route. We already constrain .page-content's own height +
  // overflow via inline style, but this project has repeatedly run into
  // cases where a layout rule silently fails to take effect somewhere in
  // the ancestor chain, and when THAT happens the whole document grows
  // taller than the viewport and the browser's own scrollbar takes over
  // — which scrolls the sidebar list and the chat panel together as one
  // page instead of each scrolling independently. Setting overflow:hidden
  // directly on <html>/<body> via JS is the most bulletproof way to
  // guarantee that can't happen, independent of any CSS specificity or
  // override issue elsewhere. Restored on unmount/route change so every
  // other page keeps its normal page-level scroll behavior.
  useEffect(() => {
    if (!isInboxRoute) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow, htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow, bodyHeight: body.style.height,
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
    };
  }, [isInboxRoute]);

  const initials = `${user?.sFname?.[0] ?? ''}${user?.sLname?.[0] ?? ''}`.toUpperCase();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="app-shell">
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <NavLink to="/dashboard" className="logo-mark">
            <span className="logo-icon">C</span>
            <span className="logo-text">CommandKit</span>
          </NavLink>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-label">Menu</div>
          {NAV.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <div className="nav-section-label">Account</div>
          {NAV_BOTTOM.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
          <button className="nav-item nav-logout" onClick={handleLogout}>
            <span className="nav-icon">⏻</span>
            <span className="nav-label">Logout</span>
          </button>
        </div>

        <div className="sidebar-user">
          <div className="avatar">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.sFname} {user?.sLname}</div>
            <div className="sidebar-user-email">{user?.sEmail}</div>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button className="topbar-hamburger" onClick={() => setSidebarOpen(true)}>
            <span /><span /><span />
          </button>
          <NavLink to="/dashboard" className="topbar-logo">CommandKit</NavLink>
          <div className="avatar avatar-sm">{initials}</div>
        </header>

        <div
          className={`page-content ${isInboxRoute ? 'page-content-full' : ''}`}
          style={isInboxRoute ? {
            padding: 0,
            maxWidth: 'none',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: 1,
            height: isMobile ? 'calc(100vh - 58px)' : '100vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
          } : undefined}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}